import React, { useContext, useEffect, useRef, useState } from 'react';

import { PlayClient } from '../search/PlayClient';
import Swipeable from '@fec/frontend/snowflakes/search/swipeable/swipeable';
import { getFilterValueByKey, translate } from '../searchHelpers';
import PlaySearchResult from '@fec/frontend/elements/search/play-search-result/play-search-result';
import PlaySearchMoreLink from '@fec/frontend/elements/search/play-search-more-link/play-search-more-link';
import { searchContextStore } from '../search/SearchContextProvider';
import { CollectionNgSwipeable } from '@fec/frontend/modules/collections/collection-ng/collection-ng-swipeable';
import {
  SWIPEABLE_CONTENT_CHANGED,
  triggerEvent,
} from '@fec/assets/js/utils/event';
import CollectionHeading from './CollectionHeading';

export const PlaySearchContainer = () => {
  // we use collection-ng-swipeable.js to initialize the swipeable component, so we need a reference to the DOM element
  const swipeableNgCollection = useRef(null);
  // we need to remember if we already initialized the CollectionNgSwipeable to avoid reinitializing the swipeable component
  const swipeabilityInitialized = useRef(false);

  const [items, setItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  const { state: searchContext } = useContext(searchContextStore);

  useEffect(() => {
    search();
  }, [searchContext.searchParams.q, searchContext.searchParams.date]);

  useEffect(() => {
    // this useEffect does 2 things:
    // 1. it initializes the swipeable component the first time it's rendered
    // 2. it triggers an event when the items change, so the swipeable component can update itself
    if (swipeableNgCollection.current && items?.count > 0) {
      if (!swipeabilityInitialized.current) {
        new CollectionNgSwipeable(swipeableNgCollection.current);
        swipeabilityInitialized.current = true;
      } else {
        triggerEvent(
          SWIPEABLE_CONTENT_CHANGED,
          null,
          swipeableNgCollection.current,
        );
      }
    }
  }, [swipeableNgCollection.current, items, swipeabilityInitialized.current]);

  const _t = translate.bind(
    null,
    searchContext.searchConfig,
    searchContext.translations,
  );

  const playSearch = new PlayClient({
    play_endpoint: searchContext.searchConfig.playSearch.endpoint,
    play_link: searchContext.searchConfig.playSearch.link,
  });

  const search = () => {
    if (searchContext.searchParams.q === '') {
      return;
    }

    playSearch
      .search(
        searchContext.searchParams.q,
        getFilterValueByKey(searchContext.searchParams.date),
      )
      .then((resultSet) => processResult(resultSet))
      .catch((reason) => {
        // eslint-disable-next-line no-console
        console.error('Error: ', reason);
      });
  };

  const processResult = (result) => {
    setItems(result);
    setSearchQuery(searchContext.searchParams.q);
  };

  const itemsToRender = items?.hits?.shows?.concat(items?.hits?.media) ?? [];
  const hasResults = itemsToRender.length > 0;

  return (
    <div
      ref={swipeableNgCollection}
      className="collection-ng js-collection-ng collection-ng--swipeable collection-ng--themed collection-ng--theme-play">
      {/* only render title area when there are results */}
      {hasResults && (
        <CollectionHeading
          title={_t('playSubtitle')}
          emphasizedTitlePart={searchQuery}
        />
      )}

      {/* always render <ul> + swipey buttons so the swipey script (non-React) doesn't lose its references */}
      <Swipeable
        translations={searchContext.translations}
        visuallyHidden={!hasResults}>
        {itemsToRender.map((item, i) => (
          <PlaySearchResult
            item={item}
            key={i}
            translations={searchContext.translations}
            index={i}
          />
        ))}

        {/* Show 2 links to play in one teaser if at least 8 results were found */}
        {itemsToRender.length > 8 && (
          <PlaySearchMoreLink
            counter={items.counter}
            translations={searchContext.translations}
            playLink={searchContext.searchConfig.playSearch.link}
            audioLink={searchContext.searchConfig.audioSearch.link}
            dateFilter={
              getFilterValueByKey(searchContext.searchParams.date).play
            }
            dateKey={searchContext.searchParams.date}
            searchQuery={searchQuery}
          />
        )}
      </Swipeable>

      {/* If no results were found, render fallback links */}
      {!hasResults && <FallbackLinks _t={_t} />}
    </div>
  );
};

const FallbackLinks = ({ _t }) => (
  <ul className="collection-ng__links collection-ng__links--no-margin-top">
    <li>
      <a
        className="collection-ng__link"
        href="/play"
        data-track-event="event_source=Collection%20Link&amp;event_name=Visit%20Link&amp;event_value=%2Fplay"
        data-referrer-track={`srg_override_mod1=play_srf&srg_override_mod2=link&srg_override_mod3=${_t(
          'goToPlay',
        )}&srg_override_mod4=`}>
        {_t('goToPlay')}
      </a>
    </li>
  </ul>
);
